import { Component, ElementRef, OnInit, ViewChild, inject } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { CapacitorGetPlatform, permissionState } from "@constants";
import { IonicModule, Platform } from "@ionic/angular";
import { StorageKey } from "@models/StorageKey";
import { TranslateModule } from "@ngx-translate/core";
import { StorageService } from "@services/storage.service";
import { Utils } from "@shared-utils/Utils";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { AnimationOptions, LottieComponent } from "ngx-lottie";

@Component({
  selector: "notification-modal",
  templateUrl: "./notification-modal.html",
  styleUrls: ["./notification-modal.scss"],
  standalone: true,
  imports: [IonicModule, TranslateModule, LottieComponent],
})
export class NotificationModalComponent implements OnInit {
  @ViewChild("notificationDialog") notificationDialog: ElementRef<HTMLDialogElement>;
  private platform = inject(Platform);
  private storageService = inject(StorageService);
  private utils = inject(Utils);
  private timesForFirstPopup = 1;
  private timesForSecondPopup = 10;
  private daysQuantityForLastPopup = 14;
  isCasino = false;
  options: AnimationOptions = {
    path: "assets/global/lottie/notification-animation.json",
  };

  ngOnInit(): void {
    this.platform.ready().then(async () => {
      if (!this.utils.isPluginAvailable("PushNotifications")) {
        return;
      }
      this.isCasino = await this.utils.isCasinoApp();
      const { receive } = await PushNotifications.checkPermissions();

      if (receive === permissionState.GRANTED) {
        const date = this.storageService.getDate(StorageKey.NOTIFICATION_PROMPT_DATE);
        if (!date) {
          this.storageService.setItem(StorageKey.NOTIFICATION_PROMPT_DATE, new Date());
        }
      }
      if (
        receive === permissionState.DENIED ||
        (Capacitor.getPlatform() === CapacitorGetPlatform.ANDROID && receive === permissionState.PROMPT)
      ) {
        const stopAskingForNotifications = this.storageService.getBoolean(
          StorageKey.STOP_ASKING_FOR_NOTIFICATIONS,
        );
        if (stopAskingForNotifications) {
          return;
        }
        this.validateLaunches();
        return;
      }
    });
  }

  goToSettings() {
    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
    this.close();
  }

  close() {
    this.notificationDialog.nativeElement.close();
  }

  /**
   * In iOS it runs this function the next launch after the user declines the notification permission.
   * In Android the status is always prompt, so we skip the first time to prevent showing the OS dialog and our dialog.
   * @returns void
   */
  private validateLaunches() {
    let launches = this.storageService.getNumber(StorageKey.NOTIFICATION_LAUNCHES);
    if (!launches) {
      this.storageService.setItem(StorageKey.NOTIFICATION_LAUNCHES, 1);
      if (Capacitor.getPlatform() === CapacitorGetPlatform.IOS) {
        this.notificationDialog.nativeElement.showModal();
      }
      return;
    }

    if (launches === this.timesForFirstPopup && Capacitor.getPlatform() === CapacitorGetPlatform.ANDROID) {
      this.notificationDialog.nativeElement.showModal();
    }

    launches += 1;
    this.storageService.setItem(StorageKey.NOTIFICATION_LAUNCHES, launches);

    if (launches === this.timesForSecondPopup) {
      this.notificationDialog.nativeElement.showModal();

      return;
    }

    const firstPromptDate = this.storageService.getDate(StorageKey.NOTIFICATION_PROMPT_DATE);
    const promptDateLimit = new Date(firstPromptDate);
    promptDateLimit.setDate(firstPromptDate.getDate() + this.daysQuantityForLastPopup);

    if (launches > this.timesForSecondPopup && new Date() > promptDateLimit) {
      this.storageService.setItem(StorageKey.STOP_ASKING_FOR_NOTIFICATIONS, true);
      this.notificationDialog.nativeElement.showModal();
      return;
    }
  }
}
